import { useIsSSR } from "@faire/web/common/server-side-rendering/isSSR";
import { optimizeImageUrl } from "@faire/web/common/transform-images";
import Color from "@faire/web/slate/Color";
import { Column, Flex } from "@faire/web/slate/Layout";
import { getSpacing, Spacer } from "@faire/web/slate/spacing";
import { responsiveMediaQueries } from "@faire/web/slate/Theme/responsiveMediaQueries";
import { useViewport } from "@faire/web/ui/hooks/useViewport";
import { observer } from "mobx-react";
import React, { useEffect, useCallback, useState, useRef } from "react";
import styled from "styled-components";

import { setTimeoutIfPossible } from "@faire/retailer/lib/async-functions/setTimeoutIfPossible";
import { circularMod } from "@faire/retailer/lib/math";
import {
  EyebrowText,
  FadingImage,
  horizontalPadding,
  Section,
} from "@faire/retailer/views/careers/__internal__/common/common";

interface IProps {
  children: React.ReactNode;
  eyebrowText: string;
  eyebrowColor?: Color;
  heroImages: string[];
  backgroundColor: string;
}

const HeroSectionBase = (props: IProps) => {
  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const isMounted = useRef(false);

  const { children, eyebrowText, eyebrowColor, heroImages, backgroundColor } =
    props;

  const resumeScrollHandler = useCallback(() => {
    setTimeoutIfPossible(() => {
      setCurrentIndex(circularMod(currentIndex + 1, props.heroImages.length));
      resumeScrollHandler();
    }, 2000);
  }, [currentIndex, props.heroImages.length]);

  useEffect(() => {
    if (isMounted.current) {
      return;
    }
    isMounted.current = true;
    resumeScrollHandler();
  }, [resumeScrollHandler]);

  const isSSR = useIsSSR();
  const { isDesktopAndAbove } = useViewport();

  return (
    <Section $backgroundColor={backgroundColor}>
      <ResponsiveContainer>
        <ContentWrapper>
          <EyebrowText color={eyebrowColor}>{eyebrowText}</EyebrowText>
          <Spacer height={isDesktopAndAbove ? "3x" : "2x"} />
          {children}
        </ContentWrapper>
        <ImageContainer>
          {heroImages.map((imageUrl, index) => (
            <HeroImage
              key={index}
              src={optimizeImageUrl({ url: imageUrl }, isSSR)}
              $isActive={index === currentIndex}
            />
          ))}
        </ImageContainer>
      </ResponsiveContainer>
    </Section>
  );
};

export const HeroSection = observer(HeroSectionBase);

const ResponsiveContainer = styled(Flex).attrs({
  direction: { mobileAndAbove: "column", tabletAndAbove: "row" },
})`
  width: 100%;
  max-width: 1660px;
  margin: 0 auto;
`;

const ContentWrapper = styled(Column).attrs({
  justify: "center",
  align: "flex-start",
})`
  ${horizontalPadding}

  ${responsiveMediaQueries.tabletAndBelow} {
    padding-top: ${getSpacing("7x")};
    padding-bottom: ${getSpacing("7x")};
  }

  ${responsiveMediaQueries.desktopAndAbove} {
    width: 50%;
    padding-right: 96px;
    padding-top: ${getSpacing("10x")};
    padding-bottom: ${getSpacing("10x")};
  }
`;

const ImageContainer = styled.div`
  position: relative;
  width: 100%;
  overflow: hidden;
  padding-top: 100%;

  ${responsiveMediaQueries.tabletAndAbove} {
    flex-shrink: 0;
    width: 50%;
    max-width: 733px;
    padding-top: initial;
  }

  ${responsiveMediaQueries.desktopAndAbove} {
    min-height: 696px;
  }
`;
const HeroImage = styled(FadingImage)`
  position: absolute;
  top: 0;
  left: 0;
  max-width: 100%;
  object-fit: cover;
  height: 100%;
  margin: auto;
`;
